<template>
    <span class="sr-only">Price list PDF data for stage {{ stageNumber }}</span>
</template>

<script>
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { isPdfPreviewMode } from '../utils/pdfPreviewMode'

const fontCache = {}
let footerPngCache = null

const PRICE_LIST_FOOTER_PNG = '/dist/images/bg/pricelist-footer.png'

async function loadPriceListFooterPng() {
    if (footerPngCache) return footerPngCache
    const res = await fetch(PRICE_LIST_FOOTER_PNG)
    if (!res.ok) throw new Error(`Could not load price list footer (${PRICE_LIST_FOOTER_PNG})`)
    const blob = await res.blob()
    const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(new Error('Failed to read footer PNG'))
        reader.readAsDataURL(blob)
    })
    const aspect = await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img.naturalWidth / img.naturalHeight)
        img.onerror = () => reject(new Error('Failed to decode footer PNG'))
        img.src = dataUrl
    })
    footerPngCache = { dataUrl, aspect }
    return footerPngCache
}

async function loadFontAsBase64(url) {
    if (fontCache[url]) return fontCache[url]
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Could not load font (${url})`)
    const buf = await res.arrayBuffer()
    const binary = new Uint8Array(buf)
    let raw = ''
    for (let i = 0; i < binary.length; i += 8192) {
        raw += String.fromCharCode.apply(null, binary.subarray(i, i + 8192))
    }
    fontCache[url] = btoa(raw)
    return fontCache[url]
}

async function registerCustomFonts(pdf) {
    const [hattonB64, interB64] = await Promise.all([
        loadFontAsBase64('/dist/fonts/pphatton.ttf'),
        loadFontAsBase64('/dist/fonts/inter.ttf')
    ])
    pdf.addFileToVFS('pphatton.ttf', hattonB64)
    pdf.addFont('pphatton.ttf', 'pp-hatton', 'normal')
    pdf.addFileToVFS('inter.ttf', interB64)
    pdf.addFont('inter.ttf', 'inter', 'normal')
    pdf.addFont('inter.ttf', 'inter', 'bold')
}

export default {
    name: 'PriceListPdf',
    mounted() {
        if (typeof window === 'undefined') return
        if (!isPdfPreviewMode()) return
        const run = () => this.previewPdf(null)
        this._devPreviewRun = run
        window.previewPdf = run
        window.previewPriceListPdf = run
        console.info(
            '[PriceListPdf] Run previewPdf() in the console to reopen the PDF (component must be mounted — click PRICE LIST once first).'
        )
    },
    beforeUnmount() {
        if (typeof window === 'undefined') return
        const run = this._devPreviewRun
        if (run && window.previewPdf === run) delete window.previewPdf
        if (run && window.previewPriceListPdf === run) delete window.previewPriceListPdf
        this._devPreviewRun = null
    },
    props: {
        stageData: {
            type: Object,
            required: true
        },
        lots: {
            type: Array,
            required: true
        }
    },
    computed: {
        stageNumber() {
            return this.stageData?.title || ''
        },
        stageTitles() {
            return this.stageData?.titles || ''
        },
        sortedLots() {
            return [...this.lots].sort((a, b) => {
                const numA = parseInt(a.title) || 0
                const numB = parseInt(b.title) || 0
                return numA - numB
            })
        }
    },
    methods: {
        formatPrice(price) {
            if (!price || price === 0) return 'POA'
            return '$' + price.toLocaleString()
        },
        drawStatusPill(pdf, text, status, x, y) {
            const colors = {
                available: [184, 214, 177],
                underOffer: [249, 210, 168],
                sold: [242, 154, 149]
            }
            const borderColors = {
                available: [116, 137, 94],
                underOffer: [229, 152, 70],
                sold: [198, 73, 66]
            }
            const textColors = {
                available: [0, 0, 0],
                underOffer: [0, 0, 0],
                sold: [0, 0, 0]
            }

            const bgColor = colors[status] || [204, 204, 204]
            const brdColor = borderColors[status] || [204, 204, 204]
            const txtColor = textColors[status] || [0, 0, 0]
            const label = (text || status || '').toUpperCase()

            pdf.setFont('inter', 'normal')
            pdf.setFontSize(4.86)
            const textWidth = pdf.getTextWidth(label)
            const pillWidth = textWidth + 5.76
            const pillHeight = 9.12
            const radius = pillHeight / 2
            const pillY = y - pillHeight / 2

            pdf.setFillColor(...bgColor)
            pdf.setDrawColor(...brdColor)
            pdf.setLineWidth(0.5)
            pdf.roundedRect(x, pillY, pillWidth, pillHeight, radius, radius, 'FD')

            pdf.setTextColor(...txtColor)
            pdf.setDrawColor(...txtColor)
            pdf.setLineWidth(0.2)
            pdf.text(label, x + pillWidth / 2, y, {
                align: 'center',
                baseline: 'middle',
                renderingMode: 'fillThenStroke'
            })
        },
        async buildPdf() {
            const sortedLots = this.sortedLots
            if (!sortedLots.length) {
                throw new Error('No lots to export')
            }

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'pt',
                format: 'a4',
                compress: true
            })
            await registerCustomFonts(pdf)
            const footerImg = await loadPriceListFooterPng()
            const pageWidth = pdf.internal.pageSize.getWidth()
            const pageHeight = pdf.internal.pageSize.getHeight()
            const marginX = 50.4
            const titleY = 35.28
            const headerBottomY = titleY + 31.2
            const footerHeightPt = pageWidth / footerImg.aspect
            const footerTableGapPt = 18
            const tableBottomMargin = footerHeightPt + footerTableGapPt

            const vm = this
            const drawPageHeader = (doc) => {
                doc.setFont('pp-hatton', 'normal')
                doc.setFontSize(20)
                doc.setTextColor(0, 0, 0)
                doc.text(`Price list - Stage ${this.stageNumber}`, marginX, titleY)

                doc.setFont('inter', 'normal')
                doc.setFontSize(8)
                doc.setTextColor('#646C5C')
                doc.text(`UPDATED     ${new Date().toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' })}`, marginX, titleY + 7, { align: 'left', baseline: 'top' })
                doc.text(`TITLES DUE  ${this.stageTitles || 'TBA'}`, marginX, titleY + 17, { align: 'left', baseline: 'top' })
            }

            autoTable(pdf, {
                startY: headerBottomY,
                margin: {
                    left: marginX,
                    right: marginX,
                    top: headerBottomY,
                    bottom: tableBottomMargin
                },
                showHead: 'everyPage',
                head: [['LOT NUMBER', 'SIZE M\u00B2', 'PRICE inc. GST', 'TYPE', 'STATUS']],
                body: sortedLots.map((lot) => {
                    const typeLabel =
                        lot.__typename === 'townhouse_Entry'
                            ? (lot.superlotType ? 'Townhouse Superlot' : 'Townhouse')
                            : String(lot.sectionTypeLabel || lot.sectionType || '-')
                    const normalizedType = typeLabel.toLowerCase() === 'home & land' ? 'House & Land' : typeLabel
                    return [
                        String(lot.title),
                        String(lot.sectionSize || 'TBA'),
                        this.formatPrice(lot.price),
                        normalizedType,
                        ''
                    ]
                }),
                theme: 'plain',
                styles: {
                    font: 'inter',
                    fontStyle: 'normal',
                    fontSize: 6.96,
                    textColor: [44, 44, 44],
                    cellPadding: { top: 3.60, right: 5, bottom: 3.60, left: 5 },
                    lineWidth: 0
                },
                headStyles: {
                    fillColor: false,
                    textColor: [233, 243, 201],
                    fontStyle: 'bold',
                    fontSize: 6,
                    cellPadding: { top: 7, right: 5, bottom: 7, left: 5 },
                    valign: 'middle',
                    lineWidth: 0
                },
                bodyStyles: {
                    lineWidth: { top: 0, bottom: 0.35, left: 0, right: 0 },
                    lineColor: [200, 200, 200]
                },
                columnStyles: {
                    0: { halign: 'left' },
                    1: { halign: 'left' },
                    2: { halign: 'left' },
                    3: { halign: 'left' },
                    4: { cellWidth: 64, halign: 'left' }
                },
                willDrawPage(data) {
                    drawPageHeader(data.doc)
                },
                didDrawPage(data) {
                    const doc = data.doc
                    const pw = doc.internal.pageSize.getWidth()
                    const ph = doc.internal.pageSize.getHeight()
                    const fh = pw / footerImg.aspect
                    doc.addImage(footerImg.dataUrl, 'PNG', 0, ph - fh, pw, fh)
                },
                willDrawCell(data) {
                    if (data.section === 'head' && data.column.index === 0) {
                        const doc = data.doc
                        const x = marginX
                        const y = data.cell.y
                        const w = pageWidth - marginX * 2
                        const h = data.cell.height
                        const r = 20 * (72 / 300)
                        doc.setFillColor(39, 51, 29)
                        doc.roundedRect(x, y, w, h, r, r, 'F')
                        doc.rect(x, y + h - r, w, r, 'F')
                    }
                },
                didParseCell(data) {
                    if (data.section === 'head') {
                        data.cell.text = []
                    }
                    if (data.section === 'body') {
                        const fill = data.row.index % 2 === 1 ? [255, 255, 255] : [242, 242, 242]
                        data.cell.styles.fillColor = fill
                        if (data.row.index === sortedLots.length - 1) {
                            const lw = data.cell.styles.lineWidth
                            if (lw && typeof lw === 'object') {
                                data.cell.styles.lineWidth = { ...lw, bottom: 0.35 }
                            }
                        }
                    }
                    if (data.section === 'body' && data.column.index === 4) {
                        data.cell.text = []
                    }
                },
                didDrawCell(data) {
                    if (data.section === 'head') {
                        const doc = data.doc
                        const headLabels = ['LOT NUMBER', 'SIZE M\u00B2', 'PRICE inc. GST', 'TYPE', 'STATUS']
                        const label = headLabels[data.column.index] || ''
                        doc.setFont('inter', 'normal')
                        doc.setFontSize(6)
                        doc.setTextColor(233, 243, 201)
                        doc.setDrawColor(233, 243, 201)
                        doc.setLineWidth(0.35)
                        const cx = data.cell.x + 4
                        const cy = data.cell.y + data.cell.height / 2
                        doc.text(label, cx, cy, { baseline: 'middle', renderingMode: 'fillThenStroke' })
                        return
                    }
                    if (data.column.index !== 4) return
                    const lot = sortedLots[data.row.index]
                    if (!lot) return
                    const doc = data.doc
                    const cx = data.cell.x + 4
                    const cy = data.cell.y + data.cell.height / 2

                    let pillLabel = lot.statusLabel || lot.status
                    if (lot.status === 'available' || (lot.statusLabel && lot.statusLabel.toLowerCase() === 'available')) {
                        pillLabel = lot.hasTitle ? 'Titles Issued' : 'For Sale'
                    }

                    vm.drawStatusPill(doc, pillLabel, lot.status, cx, cy)
                }
            })

            return pdf
        },
        async generatePdf() {
            try {
                const pdf = await this.buildPdf()
                pdf.save(`Westhaven Stage ${this.stageNumber} Price List.pdf`)
            } catch (error) {
                console.error('Error generating PDF:', error)
                alert(error.message === 'No lots to export' ? error.message : 'Error generating PDF: ' + error.message)
            }
        },
        async previewPdf(previewTab = null) {
            let blobUrl = null
            try {
                const pdf = await this.buildPdf()
                const blob = pdf.output('blob')
                blobUrl = URL.createObjectURL(blob)

                if (previewTab && !previewTab.closed) {
                    previewTab.location.href = blobUrl
                    return
                }

                const opened = window.open(blobUrl, '_blank')
                if (!opened) {
                    alert(
                        'Popup blocked — allow popups for this site, or use the download instead (turn off ?pdfPreview=1).'
                    )
                    URL.revokeObjectURL(blobUrl)
                    return
                }
            } catch (error) {
                if (blobUrl) {
                    try {
                        URL.revokeObjectURL(blobUrl)
                    } catch (_) {}
                }
                console.error('Error previewing PDF:', error)
                const msg =
                    error.message === 'No lots to export' ? error.message : 'Error previewing PDF: ' + error.message
                if (previewTab && !previewTab.closed) {
                    try {
                        previewTab.document.open()
                        previewTab.document.write(
                            `<!DOCTYPE html><meta charset="utf-8"><title>PDF preview error</title><p style="font-family:system-ui;padding:1rem">${msg.replace(/</g, '&lt;')}</p>`
                        )
                        previewTab.document.close()
                    } catch (_) {
                        try {
                            previewTab.close()
                        } catch (__) {}
                    }
                } else {
                    alert(msg)
                }
            }
        }
    }
}
</script>

<style scoped>
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>